import type { DiffResult } from './diff.js';

/**
 * The states a comparison can be in that a summary alone would misrepresent.
 *
 * Every one of these produces counts that look ordinary. Two empty documents
 * and two identical documents both report no changes; a drawing that was
 * emptied and a drawing that could not be read both report everything removed.
 * The difference matters more than the counts do, so it is named rather than
 * left for a reader to infer.
 *
 * They are not exclusive. A version that lost a sheet and left the rest alone
 * is both a page-count change and, on the remaining sheet, no change at all.
 */
export type DiffEdge =
  | 'nothing-to-compare'
  | 'zero-changes'
  | 'everything-removed'
  | 'everything-added'
  | 'page-count-change'
  | 'scale-unknown';

export const DIFF_EDGES: readonly DiffEdge[] = [
  'nothing-to-compare',
  'zero-changes',
  'everything-removed',
  'everything-added',
  'page-count-change',
  'scale-unknown',
];

/**
 * What each state entitles a reader to conclude, and — more importantly — what
 * it does not. `{n}` and `{m}` are the base and compare page counts, `{t}` the
 * tolerance with its unit.
 */
export const EDGE_TEXT: Record<DiffEdge, { en: string; ja: string }> = {
  'nothing-to-compare': {
    en: 'Nothing to compare: neither version produced any entity. This is not a statement that the two drawings are the same.',
    ja: '比較対象がありません。どちらのバージョンからも要素が得られていません。2つの図面が同一であるという意味ではありません。',
  },
  'zero-changes': {
    en: 'No difference larger than the matching tolerance {t}. Not a claim that the files are identical.',
    ja: '照合公差 {t} を超える差分はありません。ファイルが同一であるという意味ではありません。',
  },
  'everything-removed': {
    en: 'Every entity of the base version is unmatched and the compare version supplied none. Check the compare version read correctly before reading this as a deletion.',
    ja: '基準バージョンの全要素が対応なしで、比較バージョンには要素がありません。削除と判断する前に、比較バージョンが正しく読めているか確認してください。',
  },
  'everything-added': {
    en: 'Every entity of the compare version is unmatched and the base version supplied none. Check the base version read correctly before reading this as new work.',
    ja: '比較バージョンの全要素が対応なしで、基準バージョンには要素がありません。新規作成と判断する前に、基準バージョンが正しく読めているか確認してください。',
  },
  'page-count-change': {
    en: 'Sheet count changed: {n} to {m}. A sheet with no counterpart is reported whole.',
    ja: 'ページ数が {n} から {m} に変わりました。対応先のないページはページ単位で報告されます。',
  },
  'scale-unknown': {
    en: 'Physical scale was not determined. Distances are normalised page units, not millimetres.',
    ja: '尺度が未確定です。距離は正規化されたページ単位であり、ミリメートルではありません。',
  },
};

export function diffEdges(diff: DiffResult): DiffEdge[] {
  const out: DiffEdge[] = [];
  const total = diff.entries.length;

  if (diff.baseEntityCount === 0 && diff.compareEntityCount === 0) out.push('nothing-to-compare');
  else if (total > 0 && diff.summary.byClass.unchanged === total) out.push('zero-changes');

  if (diff.baseEntityCount > 0 && diff.compareEntityCount === 0) out.push('everything-removed');
  if (diff.compareEntityCount > 0 && diff.baseEntityCount === 0) out.push('everything-added');
  if (diff.basePageCount !== diff.comparePageCount) out.push('page-count-change');
  if (diff.partial) out.push('scale-unknown');

  return out;
}

/**
 * What a distance in this comparison is measured in, in the reader's language.
 *
 * Named separately because it goes into a sentence: splicing the English unit
 * into the Japanese banner left the one clause the reader most needs — that
 * these are not millimetres — half in the wrong language.
 */
export const DISPLACEMENT_UNIT_TEXT: Record<
  DiffResult['displacementUnit'],
  { en: string; ja: string }
> = {
  mm: { en: 'mm', ja: 'mm' },
  page_diagonal: { en: 'page diagonals', ja: 'ページ対角長' },
};

/** The message for one state, with the comparison's own numbers filled in. */
export function edgeMessage(edge: DiffEdge, diff: DiffResult, lang: 'en' | 'ja'): string {
  const unit = DISPLACEMENT_UNIT_TEXT[diff.displacementUnit][lang];
  return EDGE_TEXT[edge][lang]
    .replace('{n}', String(diff.basePageCount))
    .replace('{m}', String(diff.comparePageCount))
    .replace('{t}', `${diff.tolerance.toFixed(4)} ${unit}`);
}
